import axios from 'axios';
import config from '../config';

export const updateSchoolOrder = async (orderId, schoolData, businessName, businessNumber) => {
  try {
    // Handle invalid orderId
    if (!orderId) {
      console.error('Invalid order ID provided to updateSchoolOrder:', orderId);
      throw new Error('Invalid order ID. Cannot update order without a valid ID.');
    }
    
    // First, get the current order data to ensure we have the correct structure
    let currentOrder;
    try {
      const currentOrderResponse = await axios.get(
        `${config.apiUrl}/api/orders/${orderId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      currentOrder = currentOrderResponse.data;
    } catch (getError) {
      console.error('Failed to get current order data:', getError);
      // If we can't fetch the current order, we'll continue with a minimal payload
      currentOrder = { data: {} };
    }

    // Extract the order data - handle both response formats
    const orderData = currentOrder.data || currentOrder;

    // Create a minimal update payload with only the essential fields to reduce 
    // the chance of errors from missing columns in the database schema
    const updatePayload = {
      id: orderId,
      orderType: 'school',
      // Only include fields that we believe exist in the database schema
      schoolName: schoolData?.schoolInfo?.name || businessName,
      firstName: schoolData?.submittedBy?.firstName || '',
      lastName: schoolData?.submittedBy?.lastName || '',
      position: schoolData?.submittedBy?.position || '',
      email: schoolData?.submittedBy?.email || '',
      phone: schoolData?.submittedBy?.phone || '',
      numberOfFamilies: schoolData?.newsletter?.numberOfFamilies || 0,
      issuesPerTerm: schoolData?.newsletter?.issuesPerTerm || schoolData?.newsletter?.issuePerTerm || 0,
      paymentModel: schoolData?.paymentModel || 'adSupported',
    };
    
    // Include endDate if it exists in the school data
    if (schoolData?.endDate) {
      updatePayload.endDate = schoolData.endDate;
    }
    
    // Process products information
    // For backwards compatibility, include the string format
    let productsArray = [];
    if (Array.isArray(schoolData?.productsOrdered)) {
      productsArray = schoolData.productsOrdered;
    } else if (typeof schoolData?.productsOrdered === 'string') {
      if (schoolData.productsOrdered.includes(',')) {
        productsArray = schoolData.productsOrdered.split(',').map(p => p.trim());
      } else if (schoolData.productsOrdered.trim()) {
        productsArray = [schoolData.productsOrdered.trim()];
      }
    }
    
    // Add both old and new product formats
    updatePayload.productsOrdered = productsArray.join(',') || '';
    
    // Set individual product flags based on the array
    updatePayload.hasSchoolLink = productsArray.some(p => 
      p.toLowerCase() === 'schoollink' || 
      p.toLowerCase() === 'school link' || 
      p.toLowerCase() === 'school-link'
    ) ? 1 : 0;
    
    updatePayload.hasEPublisher = productsArray.some(p => 
      p.toLowerCase() === 'epublisher' || 
      p.toLowerCase() === 'e-publisher' || 
      p.toLowerCase() === 'e publisher'
    ) ? 1 : 0;
    
    updatePayload.hasPaper = productsArray.some(p => 
      p.toLowerCase() === 'paper' || 
      p.toLowerCase() === 'newspaper' || 
      p.toLowerCase() === 'print'
    ) ? 1 : 0;

    // Only include notes if it exists in the original data
    if (orderData.notes || orderData.hasOwnProperty('notes')) {
      updatePayload.notes = JSON.stringify(schoolData);
    }

    // Try the school-orders endpoint first, then fall back to orders
    const tryEndpoints = async () => {
      const endpoints = [
        `${config.apiUrl}/api/school-orders/${orderId}`,
        `${config.apiUrl}/api/orders/${orderId}`
      ];
      
      let lastError = null;
      
      for (const endpoint of endpoints) {
        try {
          const response = await axios.put(
            endpoint,
            updatePayload,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`
              }
            }
          );
          
          // Force a delay to ensure the server has time to process the update
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Return the response data directly, whether it contains order data or just a success message
          return response.data;
        } catch (error) {
          lastError = error;
          
          // Log more details about the error to help with debugging
          if (error.response) {
          }
        }
      }
      
      // If we get here, all endpoints failed
      throw lastError || new Error('All update endpoints failed');
    };
    
    return await tryEndpoints();
  } catch (error) {
    console.error('Error updating school order:', error);
    // Provide a more specific error message
    const errorMessage = error.response?.data?.message || 
                         error.response?.data?.error || 
                         error.message || 
                         'Unknown error occurred';
    throw new Error(`Failed to update school order: ${errorMessage}`);
  }
}; 